import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../../utils/api';
import { timeAgo, formatDate, PRIORITIES, CATEGORIES, ISSUE_TYPES } from '../../utils/helpers';
import { useAuth } from '../../context/AuthContext';

const TICKET_PRIORITIES = ['low', 'medium', 'high', 'critical'];

function ClientSearch({ clients, value, onChange }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [hiIdx, setHiIdx] = useState(0);
  const ref = useRef(null);
  const inputRef = useRef(null);

  const selected = clients.find(c => c.id === value);
  const filtered = query.trim()
    ? clients.filter(c => c.name.toLowerCase().includes(query.toLowerCase()))
    : clients;

  useEffect(() => { setHiIdx(0); }, [query]);

  useEffect(() => {
    function onClickOut(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', onClickOut);
    return () => document.removeEventListener('mousedown', onClickOut);
  }, []);

  function select(c) { onChange(c.id); setQuery(''); setOpen(false); }

  function handleKey(e) {
    if (!open) { if (e.key === 'ArrowDown' || e.key === 'Enter') setOpen(true); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setHiIdx(i => Math.min(i + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHiIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (filtered[hiIdx]) select(filtered[hiIdx]); }
    else if (e.key === 'Escape') { setOpen(false); setQuery(''); }
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {selected && !open ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', border: '1.5px solid #0B6623', borderRadius: 8, background: 'var(--action-bg)', cursor: 'pointer' }}
          onClick={() => { setOpen(true); setQuery(''); setTimeout(() => inputRef.current?.focus(), 0); }}>
          <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
            {selected.name}{selected.product ? ` — ${selected.product}` : ''}
          </span>
          <button type="button" onClick={e => { e.stopPropagation(); onChange(''); setQuery(''); }}
            style={{ background: 'none', border: 'none', fontSize: 16, cursor: 'pointer', color: 'var(--text-secondary)', lineHeight: 1, padding: 0 }}>×</button>
        </div>
      ) : (
        <input ref={inputRef} className="input" autoFocus={open} placeholder="Search client name…"
          value={query} onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)} onKeyDown={handleKey} />
      )}
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200, background: 'var(--bg-surface)', border: '1.5px solid var(--border-subtle)', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,.12)', maxHeight: 220, overflowY: 'auto', marginTop: 2 }}>
          {filtered.length === 0
            ? <div style={{ padding: '12px 14px', fontSize: 13, color: 'var(--text-secondary)' }}>No clients found</div>
            : filtered.map((c, i) => (
              <div key={c.id} onMouseDown={() => select(c)}
                style={{ padding: '9px 14px', fontSize: 13, cursor: 'pointer', background: i === hiIdx ? 'var(--action-bg)' : 'transparent', borderLeft: i === hiIdx ? '3px solid #0B6623' : '3px solid transparent', fontWeight: i === hiIdx ? 600 : 400 }}
                onMouseEnter={() => setHiIdx(i)}>
                <span style={{ color: 'var(--text-primary)' }}>{c.name}</span>
                {c.product && <span style={{ fontSize: 11, color: 'var(--text-secondary)', marginLeft: 6 }}>{c.product}</span>}
              </div>
            ))
          }
        </div>
      )}
    </div>
  );
}

function CreateTicketModal({ onClose, onCreated }) {
  const [clients, setClients] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [form, setForm] = useState({ on_behalf_of: '', title: '', description: '', priority: 'medium', category: 'general', product_name: '', serial_number: '', issue_type: '', location: '', contact_name: '', contact_mobile: '', started_on: '', is_recurring: 0 });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    apiFetch('/customers?limit=1000').then(d => setClients(d.customers || [])).catch(() => {});
    apiFetch('/products').then(d => setAllProducts(d.products || d || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!form.on_behalf_of) { setForm(f => ({ ...f, product_name: '', serial_number: '' })); return; }
    const client = clients.find(c => c.id === form.on_behalf_of);
    setForm(f => ({ ...f, product_name: client?.product || '', serial_number: client?.serial_number || '' }));
  }, [form.on_behalf_of, clients]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.on_behalf_of) return setError('Please select a client');
    if (!form.title.trim()) return setError('Title is required');
    if (!form.description.trim()) return setError('Description is required');
    setSaving(true); setError('');
    try {
      await apiFetch('/tickets', { method: 'POST', body: form });
      onCreated();
    } catch (err) { setError(err.message); }
    setSaving(false);
  }

  const f = (key, val) => setForm(p => ({ ...p, [key]: val }));
  const lbl = { display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 5 };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={onClose}>
      <div style={{ background: 'var(--bg-surface)', borderRadius: 16, padding: 28, width: '100%', maxWidth: 520, boxShadow: '0 20px 60px rgba(0,0,0,.2)', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Create Ticket on Behalf of Client</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text-secondary)' }}>×</button>
        </div>
        {error && <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: '#dc2626' }}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label style={lbl}>Client *</label>
              <ClientSearch clients={clients} value={form.on_behalf_of} onChange={val => f('on_behalf_of', val)} />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={lbl}>Priority</label>
                <select className="input" value={form.priority} onChange={e => f('priority', e.target.value)}>
                  {TICKET_PRIORITIES.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase()+p.slice(1)}</option>)}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={lbl}>Category</label>
                <select className="input" value={form.category} onChange={e => f('category', e.target.value)}>
                  {(CATEGORIES || ['general','technical','billing','other']).map(c => <option key={c} value={c}>{c.replace('_',' ')}</option>)}
                </select>
              </div>
            </div>
            {form.on_behalf_of && (
              <div>
                <label style={lbl}>Product *</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {allProducts.map(p => (
                    <button key={p.id} type="button"
                      onClick={() => setForm(f => ({ ...f, product_name: p.name }))}
                      style={{
                        padding: '6px 14px', borderRadius: 20, border: '1.5px solid',
                        borderColor: form.product_name === p.name ? '#0B6623' : 'var(--border-subtle)',
                        background: form.product_name === p.name ? '#0B6623' : 'var(--bg-surface)',
                        color: form.product_name === p.name ? '#fff' : 'var(--text-primary)',
                        fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all .12s',
                      }}>
                      {p.name}
                    </button>
                  ))}
                </div>
                {form.product_name && (
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 6 }}>
                    Selected: <strong style={{ color: '#0B6623' }}>{form.product_name}</strong>
                    {form.serial_number ? ` — S/N: ${form.serial_number}` : ''}
                    {' · '}
                    <span style={{ cursor: 'pointer', color: '#dc2626' }} onClick={() => setForm(f => ({ ...f, product_name: '' }))}>clear</span>
                  </div>
                )}
              </div>
            )}
            <div>
              <label style={lbl}>Title *</label>
              <input className="input" value={form.title} onChange={e => f('title', e.target.value)} placeholder="Brief issue summary" maxLength={255} required />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={lbl}>Issue type</label>
                <select className="input" value={form.issue_type} onChange={e => f('issue_type', e.target.value)}>
                  <option value="">Select…</option>
                  {ISSUE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={lbl}>Location / Branch</label>
                <input className="input" value={form.location} onChange={e => f('location', e.target.value)} placeholder="Branch / site" />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={lbl}>Contact person</label>
                <input className="input" value={form.contact_name} onChange={e => f('contact_name', e.target.value)} placeholder="Who to reach" />
              </div>
              <div style={{ flex: 1 }}>
                <label style={lbl}>Contact mobile</label>
                <input className="input" value={form.contact_mobile} onChange={e => f('contact_mobile', e.target.value)} placeholder="10-digit mobile" />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={lbl}>Started on</label>
                <input className="input" type="date" value={form.started_on} onChange={e => f('started_on', e.target.value)} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={lbl}>Recurring?</label>
                <select className="input" value={form.is_recurring} onChange={e => f('is_recurring', Number(e.target.value))}>
                  <option value={0}>No — first time</option>
                  <option value={1}>Yes — repeatedly</option>
                </select>
              </div>
            </div>
            <div>
              <label style={lbl}>Description *</label>
              <textarea className="input" value={form.description} onChange={e => f('description', e.target.value)} placeholder="Describe the issue reported by the client…" rows={5} required style={{ resize: 'vertical', fontFamily: 'inherit' }} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button type="button" onClick={onClose} style={{ flex: 1, padding: 10, borderRadius: 8, border: '1.5px solid var(--border-subtle)', background: 'var(--bg-surface)', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', cursor: 'pointer' }}>Cancel</button>
            <button type="submit" disabled={saving} style={{ flex: 1, padding: 10, borderRadius: 8, border: 'none', background: '#0B6623', fontSize: 13, fontWeight: 700, color: '#fff', cursor: 'pointer' }}>{saving ? 'Creating…' : 'Create Ticket'}</button>
          </div>
        </form>
        <p style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 12, marginBottom: 0 }}>Ticket will be assigned to you. You can forward it to a manager after creation.</p>
      </div>
    </div>
  );
}

function ForwardModal({ ticket, onClose, onDone }) {
  const [managers, setManagers] = useState([]);
  const [managerId, setManagerId] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    apiFetch('/users/agents').then(d => setManagers((d || []).filter(u => ['manager','admin'].includes(u.role)))).catch(() => {});
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      await apiFetch(`/tickets/${ticket.id}/forward`, { method: 'POST', body: { manager_id: managerId || undefined, note } });
      onDone();
    } catch (err) { setError(err.message); }
    setSaving(false);
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={onClose}>
      <div style={{ background: 'var(--bg-surface)', borderRadius: 16, padding: 28, width: '100%', maxWidth: 420, boxShadow: '0 20px 60px rgba(0,0,0,.2)' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ fontSize: 17, fontWeight: 700, margin: 0 }}>Forward Ticket to Manager</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text-secondary)' }}>×</button>
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16, padding: '10px 14px', background: 'var(--bg-app)', borderRadius: 8 }}>
          <strong style={{ color: 'var(--text-primary)' }}>{ticket.ticket_number}</strong> — {ticket.title}
        </div>
        {error && <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: '#dc2626' }}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 5 }}>Forward To</label>
              <select className="input" value={managerId} onChange={e => setManagerId(e.target.value)}>
                <option value="">— Auto-assign to available manager —</option>
                {managers.map(m => <option key={m.id} value={m.id}>{m.name} ({m.role})</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 5 }}>Note (optional)</label>
              <textarea className="input" value={note} onChange={e => setNote(e.target.value)} placeholder="Reason for forwarding…" rows={3} style={{ resize: 'vertical', fontFamily: 'inherit' }} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button type="button" onClick={onClose} style={{ flex: 1, padding: 10, borderRadius: 8, border: '1.5px solid var(--border-subtle)', background: 'var(--bg-surface)', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', cursor: 'pointer' }}>Cancel</button>
            <button type="submit" disabled={saving} style={{ flex: 1, padding: 10, borderRadius: 8, border: 'none', background: '#0284c7', fontSize: 13, fontWeight: 700, color: '#fff', cursor: 'pointer' }}>{saving ? 'Forwarding…' : '↗ Forward to Manager'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

const GROUPS = [
  { key: 'open',       label: 'Open',            statuses: ['open'],               color: 'var(--status-open-color)', bg: 'var(--status-open-bg)', icon: '🔴' },
  { key: 'in_progress', label: 'In Progress',    statuses: ['in_progress'],        color: 'var(--status-progress-color)', bg: 'var(--status-progress-bg)', icon: '🟡' },
  { key: 'closed',     label: 'Closed / Resolved', statuses: ['resolved', 'closed'], color: 'var(--status-resolved-color)', bg: 'var(--status-resolved-bg)', icon: '🟢' },
];

function TicketRow({ t }) {
  const breached = t.due_at && new Date(t.due_at) < new Date() && !['resolved', 'closed'].includes(t.status);
  const isClosed = ['resolved', 'closed'].includes(t.status);

  // SLA display
  let slaLabel = null, slaColor = 'var(--text-secondary)', slaBg = 'var(--n-100)', slaBorder = 'var(--border-subtle)';
  if (t.due_at) {
    if (breached) {
      slaLabel = '⚠ SLA Breached';
      slaColor = '#b91c1c'; slaBg = 'var(--red-50)'; slaBorder = '#fca5a5';
    } else if (!isClosed) {
      const hoursLeft = (new Date(t.due_at) - new Date()) / 36e5;
      if (hoursLeft < 4) {
        slaLabel = `⏰ Due in ${Math.max(0, Math.round(hoursLeft))}h`;
        slaColor = '#92400e'; slaBg = 'var(--amber-100)'; slaBorder = '#fde68a';
      } else {
        slaLabel = `SLA: ${formatDate(t.due_at)}`;
      }
    } else {
      slaLabel = `SLA: ${formatDate(t.due_at)}`;
      slaColor = '#094d1e'; slaBg = 'var(--action-bg)'; slaBorder = 'var(--action-hover)';
    }
  }

  return (
    <Link to={`/agent/tickets/${t.id}`} style={{ textDecoration: 'none' }}>
      <div className="card" style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14, background: breached ? 'var(--action-bg)' : 'var(--bg-surface)', transition: 'box-shadow .15s', borderRadius: 10 }}
        onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,.1)'}
        onMouseLeave={e => e.currentTarget.style.boxShadow = ''}>

        {/* Left: ticket meta */}
        <div style={{ minWidth: 0, width: 220, flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5, flexWrap: 'wrap' }}>
            <span style={{ background: '#0B6623', color: '#fff', borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 700, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
              {t.ticket_number}
            </span>
            <span className={`badge badge-${t.status}`}>{t.status.replace('_', ' ')}</span>
            <span className={`badge badge-${t.priority}`}>{t.priority}</span>
          </div>
          <p style={{ fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', margin: 0, color: 'var(--text-main)' }}>{t.title}</p>
          <p style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 3, marginBottom: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {t.company_name ? <><strong>{t.company_name}</strong> · {t.customer_name}</> : t.customer_name} · {timeAgo(t.updated_at)}
          </p>
        </div>

        {/* Middle: description preview */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {t.description ? (
            <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 8, padding: '10px 14px', fontSize: 15, fontStyle: 'italic', color: 'var(--text-muted)', lineHeight: 1.5, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', textOverflow: 'ellipsis', textAlign: 'center' }}>
              "{t.description}"
            </div>
          ) : (
            <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 8, padding: '10px 14px', fontSize: 14, color: '#cbd5e1', fontStyle: 'italic', textAlign: 'center' }}>
              No description provided.
            </div>
          )}
        </div>

        {/* Right: product + SLA + date */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 5, flexShrink: 0, minWidth: 130 }}>
          {t.product_name && (
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--status-resolved-text)', background: 'var(--action-bg)', border: '1px solid var(--action-hover)', borderRadius: 999, padding: '3px 10px', whiteSpace: 'nowrap' }}>
              {t.product_name}
            </span>
          )}
          {slaLabel && (
            <span style={{ fontSize: 11, fontWeight: 600, color: slaColor, background: slaBg, border: `1px solid ${slaBorder}`, borderRadius: 999, padding: '3px 10px', whiteSpace: 'nowrap' }}>
              {slaLabel}
            </span>
          )}
          <span style={{ fontSize: 11, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{formatDate(t.created_at)}</span>
        </div>
      </div>
    </Link>
  );
}

export default function AgentTickets() {
  const { user } = useAuth();
  const [data, setData]       = useState({ tickets: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [priority, setPriority] = useState('');
  const [expanded, setExpanded] = useState({ open: true, in_progress: true, closed: false });
  const [showCreate, setShowCreate] = useState(false);
  const [showForward, setShowForward] = useState(null);

  function loadTickets() {
    setLoading(true);
    apiFetch('/tickets').then(setData).finally(() => setLoading(false));
  }

  useEffect(() => { loadTickets(); }, []);

  function toggle(key) {
    setExpanded(e => ({
      open: key === 'open' ? !e[key] : false,
      in_progress: key === 'in_progress' ? !e[key] : false,
      closed: key === 'closed' ? !e[key] : false,
    }));
  }

  const filtered = data.tickets.filter(t => {
    if (priority && t.priority !== priority) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      if (!t.title?.toLowerCase().includes(q) && !t.ticket_number?.toLowerCase().includes(q) && !t.customer_name?.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const grouped = GROUPS.map(g => ({
    ...g,
    tickets: filtered.filter(t => g.statuses.includes(t.status)),
  }));

  const open       = data.tickets.filter(t => t.status === 'open').length;
  const inProgress = data.tickets.filter(t => t.status === 'in_progress').length;
  const closed     = data.tickets.filter(t => ['resolved', 'closed'].includes(t.status)).length;

  return (
    <div className="page">
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 className="page-title">My Assigned Tickets</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginTop: 2 }}>Welcome, {user.name} — {data.total} tickets assigned to you</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)} style={{ fontSize: 13 }}>+ New Ticket</button>
      </div>

      {/* Summary strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 24 }}>
        {[
          { key: 'open',        label: 'Open',              value: open,       color: 'var(--status-open-color)', bg: 'var(--status-open-bg)' },
          { key: 'in_progress', label: 'In Progress',       value: inProgress, color: 'var(--status-progress-color)', bg: 'var(--status-progress-bg)' },
          { key: 'closed',      label: 'Closed / Resolved', value: closed,     color: 'var(--status-resolved-color)', bg: 'var(--status-resolved-bg)' },
        ].map(s => (
          <div key={s.label} className="card" onClick={() => toggle(s.key)} style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer', outline: expanded[s.key] ? `2px solid ${s.color}` : 'none', transition: 'outline .15s' }}>
            <div style={{ width: 52, height: 52, borderRadius: 12, background: s.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, fontWeight: 800, color: s.color }}>{s.value}</div>
            <span style={{ fontSize: 15, color: expanded[s.key] ? s.color : 'var(--text-secondary)', fontWeight: 600 }}>{s.label}</span>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        <input className="input" style={{ maxWidth: 280 }} placeholder="Search by title, ticket no. or customer…"
          value={search} onChange={e => setSearch(e.target.value)} />
        <select className="input" style={{ maxWidth: 160 }} value={priority} onChange={e => setPriority(e.target.value)}>
          <option value="">All Priorities</option>
          {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><span className="spinner" /></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {grouped.map((group, idx) => (
            <div key={group.key}>
              {/* Group header */}
              <button
                onClick={() => toggle(group.key)}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '11px 16px', background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: expanded[group.key] ? '10px 10px 0 0' : 10, cursor: 'pointer', transition: 'all .15s' }}
              >
                {/* +/- toggle indicator */}
                <div style={{ width: 22, height: 22, borderRadius: 6, background: expanded[group.key] ? 'var(--n-200)' : 'var(--n-100)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 700, color: 'var(--text-muted)', flexShrink: 0, lineHeight: 1 }}>
                  {expanded[group.key] ? '−' : '+'}
                </div>
                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-main)' }}>
                  {idx + 1}. {group.label}
                </span>
                <span style={{ fontSize: 12, fontWeight: 600, background: group.bg, color: group.color, borderRadius: 99, padding: '2px 10px', marginLeft: 2 }}>
                  {group.tickets.length}
                </span>
                <span style={{ marginLeft: 'auto', fontSize: 11, color: '#94a3b8' }}>
                  {expanded[group.key] ? 'Click to collapse' : 'Click to expand'}
                </span>
              </button>

              {/* Ticket list */}
              {expanded[group.key] && (
                <div style={{ border: '1px solid var(--border-subtle)', borderTop: 'none', borderRadius: '0 0 10px 10px', overflow: 'hidden' }}>
                  {group.tickets.length === 0 ? (
                    <div style={{ padding: '28px 20px', textAlign: 'center', color: '#94a3b8', fontSize: 13, background: 'var(--bg-surface)' }}>
                      No {group.label.toLowerCase()} tickets.
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                      {group.tickets.map((t, i) => (
                        <div key={t.id} style={{ borderTop: i === 0 ? 'none' : '1px solid var(--n-100)', padding: '6px 10px', display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ flex: 1, minWidth: 0 }}><TicketRow t={t} /></div>
                          {!['resolved','closed'].includes(t.status) && (
                            <button onClick={() => setShowForward(t)}
                              style={{ flexShrink: 0, fontSize: 11, padding: '4px 10px', borderRadius: 6, border: '1px solid #bfdbfe', background: 'var(--blue-50)', color: 'var(--blue-700)', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                              ↗ Forward
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <CreateTicketModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); loadTickets(); }} />
      )}
      {showForward && (
        <ForwardModal ticket={showForward} onClose={() => setShowForward(null)} onDone={() => { setShowForward(null); loadTickets(); }} />
      )}
    </div>
  );
}
